<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'registers',
    path: '/registers',
    component: 'RegisterView',
    title: () => pgettext('Register', 'Registers'),
  });
  addRoute({
    name: 'letterRegister',
    path: '/letter-register',
    component: 'RegisterView',
    title: () => pgettext('Register', 'Registers'),
  });
  addRoute({
    name: 'invoiceRegister',
    path: '/invoice-register',
    component: 'RegisterView',
    title: () => pgettext('Register', 'Registers'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RegisterView',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    return {
      sort_mode: 'desc',
      register_id: selectedRegisterId.value,
      version_id: selectedVersionId.value,
    };
  };
  const invoiceTypeBadgeClass = (...args) => {
    const [row] = args;
    const cc = $config.value.invoice.types.colorCodes[row.invoice_type];
    return `bg-${cc.bg} text-${cc.text}`;
  };
  const priorityBadgeClass = (...args) => {
    const [row] = args;
    const priority = row.priority;

    if (priority == 1) {
      return `border border-purple-500 text-purple-500 py-0`;
    }

    if (priority == 2) {
      return `border border-red-500 text-red-500 py-0`;
    }
  };
  const invoiceFormatter = (...args) => {
    const [grid] = args;
    return grid.ALL_FORMATTERS.chain({
      formatters: [
        {
          name: 'template',
          format: '{{ document.invoice_number }}',
        },
        {
          name: 'link',
          href: '#',
          className: 'command-link',
        },
        {
          name: 'cellclass',
          value: (row) => ({
            'fw-bold': true,
            'bg-yellow-500 text-yellow-800': row.is_corrected,
            'bg-red-300 text-red-800': row.is_reversed,
          }),
        },
      ],
    });
  };
  const referenceNumberFormatter = (...args) => {
    const [grid] = args;
    return grid.ALL_FORMATTERS.chain({
      formatters: [
        {
          name: 'getvalue',
          field: 'reference_number',
          source: 'row',
        },
        {
          name: 'cellclass',
          value: (row) => ({
            'fw-bold': true,
          }),
        },
      ],
    });
  };
  const documentFormatter = (...args) => {
    const [row, cell, value, columnDef, data, grid] = args;
    if (value && value.invoice_number) {
      return invoiceFormatter(grid)(...args);
    }

    if (data.reference_number) {
      return referenceNumberFormatter(grid)(
        row,
        cell,
        data.reference_number,
        columnDef,
        data,
        grid
      );
    }
  };

  const states = ref({
    selectedRegisterId: null,
    selectedVersionId: null,
  });
  const selectedItems = ref([]);
  const statuses = ref(null);
  const selectedStatus = ref(null);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    filterable: true,
    resizable: true,
    selectable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'entry_number',
      name: $fn.value.pgettext('Register', 'No.'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'link',
              href: '#',
            },
            {
              name: 'cellclass',
              value: (row) => ({
                'fw-bold': true,
                'bg-yellow-200': !row.locked,
                'bg-gray-dark-100': row.locked,
                'text-decoration-line-through': row.void,
              }),
            },
          ],
        },
      ],
    },
    {
      field: 'entry_date',
      name: $fn.value.pgettext('Register', 'Date'),
      type: 'date',
    },
    {
      field: 'direction',
      name: $fn.value.pgettext('Register', 'Direction'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'RegisterEntryDirection',
            conversion: 'StartCase',
            filterInput: true,
          },
        ],
      },
    },
    {
      field: 'document_type',
      name: $fn.value.pgettext('Register', 'Document type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'RegisterDocumentType',
            conversion: 'StartCase',
            filterInput: true,
          },
        ],
      },
    },
    {
      field: 'attachments',
      name: $fn.value.pgettext('Register', 'Attachments'),
      type: 'command',
      formatter: {
        name: 'attachment',
      },
    },
    {
      field: 'delivery_modes',
      name: $fn.value.pgettext('Register', 'Delivery mode'),
      formatter: {
        name: 'join',
        separator: ', ',
      },
    },
    {
      field: 'clerk',
      name: $fn.value.pgettext('Register', 'Clerk'),
    },
    {
      field: 'project',
      name: $fn.value.pgettext('Register', 'Project'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'code,name',
              source: 'value',
              separator: ' • ',
            },
          },
          {
            name: 'routeLink',
            to: 'project',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'priority',
      name: $fn.value.pgettext('Register', 'Priority'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'mapvalue',
            mapping: {
              0: $fn.value.pgettext('Priority', 'normal'),
              1: $fn.value.pgettext('Priority', 'important'),
              2: $fn.value.pgettext('Priority', 'urgent'),
            },
          },
          {
            name: 'badge',
            pill: true,
            badgeClass: priorityBadgeClass,
          },
        ],
      },
    },
    {
      field: 'document',
      name: $fn.value.pgettext('Register', 'Document'),
      type: 'command',
      formatter: documentFormatter,
    },
    {
      field: 'partner',
      name: $fn.value.pgettext('Register', 'Partner'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'subject',
      name: $fn.value.pgettext('Register', 'Subject'),
    },
    {
      field: 'ref_entry_number',
      name: $fn.value.pgettext('Register', 'Ref.'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'link',
              href: '#',
            },
          ],
        },
      ],
    },
    {
      field: 'status',
      name: $fn.value.pgettext('Register', 'Status'),
    },
    {
      field: 'categories',
      name: $fn.value.pgettext('Register', 'Category'),
    },
    {
      field: 'delivery_date',
      name: $fn.value.pgettext('Register', 'Delivery date'),
      type: 'date',
    },
    {
      field: 'notes',
      name: $fn.value.pgettext('Register', 'Notes'),
    },
  ]);

  const $data = computed(() => ({
    states: states.value,
    selectedItems: selectedItems.value,
    statuses: statuses.value,
    selectedStatus: selectedStatus.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_statuses = async () => {
    return $fn.value
      .fetch('get_tags', { parameters: { tag_type: 'register-entry:status' } })
      .then((data) => _.map(data, (p) => p.name));
  };
  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_register_entries', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'states', cache: { type: 'local', key: 'RegisterView.states' } },
      { name: 'selectedItems' },
      { name: 'statuses' },
      { name: 'selectedStatus' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { statuses: $$load_statuses, ds: $$load_ds },
    ux: {},
    dataRefs: {
      states,
      selectedItems,
      statuses,
      selectedStatus,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedRegisterId = computed(() => {
    if (loaded.value) {
      return states.value.selectedRegisterId;
    }
    return null;
  });
  const selectedVersionId = computed(() => {
    if (loaded.value) {
      return states.value.selectedVersionId;
    }
    return null;
  });
  const isRegisterSelected = computed(() => {
    return selectedRegisterId.value && selectedVersionId.value;
  });
  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('Register', 'Registers'),
      icon: $config.value.register.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_s4jqu4ibr_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_reloadSlickgrid_t7998brrb_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2248226175642056_reloadSlickgrid_vs0iznnox_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const inst_evh_2248226175642056_close_x78rgqn35_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2248226175642056_showModal_owojmd3y7_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_edo7ed6f1_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'entry_number';
  };
  const act_2248226175642056_reloadSlickgrid_pk7krlhdd_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2248226175642056_reloadSlickgrid_gn753q9dq_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const inst_evh_2248226175642056_close_y3zwhbl6w_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2248226175642056_showModal_rgp5hcnkk_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        oid: $event.data.data.ref_entry_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_hvxe4xg40_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'ref_entry_number';
  };
  const act_2248226175642056_script_7ticcmpf6_value_callable = async (
    $event
  ) => {
    return $app.value.attachments.downloadAttachmentFromSlickCommand($event);
  };
  const act_2248226175642056_script_7ticcmpf6_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_7ticcmpf6_value_callable($event);
      },
    };
  };
  const inst_evh_2248226175642056_command_nlvz4qo1f_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'attachments';
  };
  const act_2248226175642056_reloadSlickgrid_djs0suvl7_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2248226175642056_reloadSlickgrid_1ba30i0m9_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const inst_evh_2248226175642056_close_xcopprcjg_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2248226175642056_showModal_vdckpwe1o_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.document.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_hu2kiqi4u_getWhen = async (
    $event
  ) => {
    return (
      $event.data.column.id === 'document' &&
      !!$event.data.data.document &&
      !!$event.data.data.document.invoice_number
    );
  };
  const act_2248226175642056_setData_lloj6hm6e_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_setData_xbih3apxm_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'states',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_reloadSlickgrid_gu8osd38n_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2095089031469157_reloadSlickgrid_iv1kvz93p_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2095089031469157_reloadSlickgrid_0amksw2m8_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const inst_evh_2095089031469157_close_tr62b1ajj_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2095089031469157_showModal_vneypey3g_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        registerId: states.value.selectedRegisterId,
        versionId: states.value.selectedVersionId,
      },
    };
  };
  const act_7439928539205587_setData_pe5eglu03_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedStatus',
      value: $event.scope.status,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7439928539205587_reloadSlickgrid_r8klk2b1j_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_7439928539205587_request_y7vuhsz6s_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_register_entry',
      data: _.map(selectedItems.value, (p) => ({
        id: p.id,
        status: selectedStatus.value,
      })),
    };
  };
  const act_2155315188409751_setData_1s23x40ro_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedStatus',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2155315188409751_reloadSlickgrid_sn8o843pj_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2155315188409751_request_ghsidpzrp_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_register_entry',
      data: _.map(selectedItems.value, (p) => ({
        id: p.id,
        status: selectedStatus.value,
      })),
    };
  };

  const act_7315092382398562_closeModal_s4jqu4ibr =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_s4jqu4ibr_getActionArgs,
    });
  const inst_evh_7315092382398562_close_rhgf9k7rf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_s4jqu4ibr,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_rhgf9k7rf],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_9g3u6fo6g =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ncmfp5384 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9g3u6fo6g,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ncmfp5384],
    event: 'reload',
  });
  const act_2248226175642056_reloadSlickgrid_t7998brrb =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2248226175642056_reloadSlickgrid_t7998brrb_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2248226175642056_saved_lwe4vjaqd =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadSlickgrid_t7998brrb,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_2248226175642056_reloadSlickgrid_vs0iznnox =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2248226175642056_reloadSlickgrid_vs0iznnox_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2248226175642056_close_x78rgqn35 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadSlickgrid_vs0iznnox,
      event: 'close',
      when: inst_evh_2248226175642056_close_x78rgqn35_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_2248226175642056_showModal_owojmd3y7 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_owojmd3y7_getActionArgs,
      displayName: 'showRegisterEntryModal',
      events: [
        inst_evh_2248226175642056_saved_lwe4vjaqd,
        inst_evh_2248226175642056_close_x78rgqn35,
      ],
    });
  const inst_evh_2248226175642056_command_edo7ed6f1 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_owojmd3y7,
      event: 'command',
      when: inst_evh_2248226175642056_command_edo7ed6f1_getWhen,
      displayName: 'showRegisterEntryModal',
    });
  const act_2248226175642056_reloadSlickgrid_pk7krlhdd =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2248226175642056_reloadSlickgrid_pk7krlhdd_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2248226175642056_saved_6jxz00fb0 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadSlickgrid_pk7krlhdd,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_2248226175642056_reloadSlickgrid_gn753q9dq =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2248226175642056_reloadSlickgrid_gn753q9dq_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2248226175642056_close_y3zwhbl6w =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadSlickgrid_gn753q9dq,
      event: 'close',
      when: inst_evh_2248226175642056_close_y3zwhbl6w_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_2248226175642056_showModal_rgp5hcnkk =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_rgp5hcnkk_getActionArgs,
      displayName: 'showRefEntryModal',
      events: [
        inst_evh_2248226175642056_saved_6jxz00fb0,
        inst_evh_2248226175642056_close_y3zwhbl6w,
      ],
    });
  const inst_evh_2248226175642056_command_hvxe4xg40 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_rgp5hcnkk,
      event: 'command',
      when: inst_evh_2248226175642056_command_hvxe4xg40_getWhen,
      displayName: 'showRefEntryModal',
    });
  const act_2248226175642056_script_7ticcmpf6 = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_7ticcmpf6_getActionArgs,
  });
  const inst_evh_2248226175642056_command_nlvz4qo1f =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_7ticcmpf6,
      event: 'command',
      when: inst_evh_2248226175642056_command_nlvz4qo1f_getWhen,
      displayName: 'script',
    });
  const act_2248226175642056_reloadSlickgrid_djs0suvl7 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2248226175642056_reloadSlickgrid_djs0suvl7_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2248226175642056_saved_qpgxtwq7h =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadSlickgrid_djs0suvl7,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_2248226175642056_reloadSlickgrid_1ba30i0m9 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2248226175642056_reloadSlickgrid_1ba30i0m9_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2248226175642056_close_xcopprcjg =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadSlickgrid_1ba30i0m9,
      event: 'close',
      when: inst_evh_2248226175642056_close_xcopprcjg_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_2248226175642056_showModal_vdckpwe1o =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_vdckpwe1o_getActionArgs,
      displayName: 'showInvoiceModal',
      events: [
        inst_evh_2248226175642056_saved_qpgxtwq7h,
        inst_evh_2248226175642056_close_xcopprcjg,
      ],
    });
  const inst_evh_2248226175642056_command_hu2kiqi4u =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_vdckpwe1o,
      event: 'command',
      when: inst_evh_2248226175642056_command_hu2kiqi4u_getWhen,
      displayName: 'showInvoiceModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_edo7ed6f1,
      inst_evh_2248226175642056_command_hvxe4xg40,
      inst_evh_2248226175642056_command_nlvz4qo1f,
      inst_evh_2248226175642056_command_hu2kiqi4u,
    ],
    event: 'command',
  });
  const act_2248226175642056_setData_lloj6hm6e = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_lloj6hm6e_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_jntax32d6 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_lloj6hm6e,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_jntax32d6],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_setData_xbih3apxm = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_xbih3apxm_getActionArgs,
  });
  const inst_evh_4615685607825208_updateModelValue_t9slwvvn9 =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_xbih3apxm,
      event: 'update:modelValue',
      displayName: 'setData',
    });
  const act_4615685607825208_reloadSlickgrid_gu8osd38n =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_4615685607825208_reloadSlickgrid_gu8osd38n_getActionArgs,
    });
  const inst_evh_4615685607825208_updateModelValue_pxdnumqmx =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadSlickgrid_gu8osd38n,
      event: 'update:modelValue',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_4615685607825208_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_4615685607825208_updateModelValue_t9slwvvn9,
        inst_evh_4615685607825208_updateModelValue_pxdnumqmx,
      ],
      event: 'update:modelValue',
    });
  const act_2095089031469157_reloadSlickgrid_iv1kvz93p =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2095089031469157_reloadSlickgrid_iv1kvz93p_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2095089031469157_saved_x08z6nv8m =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadSlickgrid_iv1kvz93p,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_2095089031469157_reloadSlickgrid_0amksw2m8 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2095089031469157_reloadSlickgrid_0amksw2m8_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2095089031469157_close_tr62b1ajj =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadSlickgrid_0amksw2m8,
      event: 'close',
      when: inst_evh_2095089031469157_close_tr62b1ajj_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_2095089031469157_showModal_vneypey3g =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_vneypey3g_getActionArgs,
      events: [
        inst_evh_2095089031469157_saved_x08z6nv8m,
        inst_evh_2095089031469157_close_tr62b1ajj,
      ],
    });
  const inst_evh_2095089031469157_click_e2jr658v1 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_vneypey3g,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_e2jr658v1],
    event: 'click',
  });
  const act_7439928539205587_setData_pe5eglu03 = new actionTypes.SetDataAction({
    actionArgs: act_7439928539205587_setData_pe5eglu03_getActionArgs,
  });
  const inst_evh_7439928539205587_clickPreventStop_o16cjrtyc =
    new actionTypes.EventHandlerImpl({
      action: act_7439928539205587_setData_pe5eglu03,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_7439928539205587_reloadSlickgrid_r8klk2b1j =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_7439928539205587_reloadSlickgrid_r8klk2b1j_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_7439928539205587_success_2anfr6nda =
    new actionTypes.EventHandlerImpl({
      action: act_7439928539205587_reloadSlickgrid_r8klk2b1j,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_7439928539205587_request_y7vuhsz6s = new actionTypes.RequestAction({
    actionArgs: act_7439928539205587_request_y7vuhsz6s_getActionArgs,
    displayName: 'setStatus',
    events: [inst_evh_7439928539205587_success_2anfr6nda],
  });
  const inst_evh_7439928539205587_clickPreventStop_c5ppobli3 =
    new actionTypes.EventHandlerImpl({
      action: act_7439928539205587_request_y7vuhsz6s,
      event: 'click.prevent.stop',
      displayName: 'setStatus',
    });
  const inst_evh_7439928539205587_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_7439928539205587_clickPreventStop_o16cjrtyc,
        inst_evh_7439928539205587_clickPreventStop_c5ppobli3,
      ],
      event: 'click.prevent.stop',
    });
  const act_2155315188409751_setData_1s23x40ro = new actionTypes.SetDataAction({
    actionArgs: act_2155315188409751_setData_1s23x40ro_getActionArgs,
  });
  const inst_evh_2155315188409751_clickPreventStop_nmheguilt =
    new actionTypes.EventHandlerImpl({
      action: act_2155315188409751_setData_1s23x40ro,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const act_2155315188409751_reloadSlickgrid_sn8o843pj =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2155315188409751_reloadSlickgrid_sn8o843pj_getActionArgs,
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_2155315188409751_success_l8bvjx43g =
    new actionTypes.EventHandlerImpl({
      action: act_2155315188409751_reloadSlickgrid_sn8o843pj,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2155315188409751_request_ghsidpzrp = new actionTypes.RequestAction({
    actionArgs: act_2155315188409751_request_ghsidpzrp_getActionArgs,
    displayName: 'setStatus',
    events: [inst_evh_2155315188409751_success_l8bvjx43g],
  });
  const inst_evh_2155315188409751_clickPreventStop_fhku6lbcu =
    new actionTypes.EventHandlerImpl({
      action: act_2155315188409751_request_ghsidpzrp,
      event: 'click.prevent.stop',
      displayName: 'setStatus',
    });
  const inst_evh_2155315188409751_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_2155315188409751_clickPreventStop_nmheguilt,
        inst_evh_2155315188409751_clickPreventStop_fhku6lbcu,
      ],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_rhgf9k7rf,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_ncmfp5384,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_saved_lwe4vjaqd,
    reloadSlickgrid: inst_evh_2248226175642056_saved_lwe4vjaqd,
    inst_evh_2248226175642056_close_x78rgqn35,
    inst_evh_2248226175642056_command_edo7ed6f1,
    showRegisterEntryModal: inst_evh_2248226175642056_command_edo7ed6f1,
    inst_evh_2248226175642056_saved_6jxz00fb0,
    inst_evh_2248226175642056_close_y3zwhbl6w,
    inst_evh_2248226175642056_command_hvxe4xg40,
    showRefEntryModal: inst_evh_2248226175642056_command_hvxe4xg40,
    inst_evh_2248226175642056_command_nlvz4qo1f,
    inst_evh_2248226175642056_saved_qpgxtwq7h,
    inst_evh_2248226175642056_close_xcopprcjg,
    inst_evh_2248226175642056_command_hu2kiqi4u,
    showInvoiceModal: inst_evh_2248226175642056_command_hu2kiqi4u,
    inst_evh_2248226175642056_command,
    inst_evh_2248226175642056_selectedRowsChanged_jntax32d6,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_updateModelValue_t9slwvvn9,
    inst_evh_4615685607825208_updateModelValue_pxdnumqmx,
    inst_evh_4615685607825208_updateModelValue,
    inst_evh_2095089031469157_saved_x08z6nv8m,
    inst_evh_2095089031469157_close_tr62b1ajj,
    inst_evh_2095089031469157_click_e2jr658v1,
    inst_evh_2095089031469157_click,
    inst_evh_7439928539205587_clickPreventStop_o16cjrtyc,
    inst_evh_7439928539205587_success_2anfr6nda,
    inst_evh_7439928539205587_clickPreventStop_c5ppobli3,
    setStatus: inst_evh_7439928539205587_clickPreventStop_c5ppobli3,
    inst_evh_7439928539205587_clickPreventStop,
    inst_evh_2155315188409751_clickPreventStop_nmheguilt,
    inst_evh_2155315188409751_success_l8bvjx43g,
    inst_evh_2155315188409751_clickPreventStop_fhku6lbcu,
    inst_evh_2155315188409751_clickPreventStop,
  };

  const evh_7315092382398562_close_rhgf9k7rf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_rhgf9k7rf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_ncmfp5384 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ncmfp5384.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_lwe4vjaqd = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_lwe4vjaqd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_x78rgqn35 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_x78rgqn35.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_edo7ed6f1 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_edo7ed6f1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_6jxz00fb0 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_6jxz00fb0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_y3zwhbl6w = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_y3zwhbl6w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_hvxe4xg40 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_hvxe4xg40.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_nlvz4qo1f = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_nlvz4qo1f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_qpgxtwq7h = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_qpgxtwq7h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_xcopprcjg = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_xcopprcjg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_hu2kiqi4u = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_hu2kiqi4u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_jntax32d6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_jntax32d6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue_t9slwvvn9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_updateModelValue_t9slwvvn9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue_pxdnumqmx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_updateModelValue_pxdnumqmx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_saved_x08z6nv8m = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_saved_x08z6nv8m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_tr62b1ajj = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_tr62b1ajj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_e2jr658v1 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_e2jr658v1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_clickPreventStop_o16cjrtyc = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7439928539205587_clickPreventStop_o16cjrtyc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_success_2anfr6nda = async (event, scope) => {
    return await actions.inst_evh_7439928539205587_success_2anfr6nda.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_clickPreventStop_c5ppobli3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7439928539205587_clickPreventStop_c5ppobli3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7439928539205587_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7439928539205587_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2155315188409751_clickPreventStop_nmheguilt = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2155315188409751_clickPreventStop_nmheguilt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2155315188409751_success_l8bvjx43g = async (event, scope) => {
    return await actions.inst_evh_2155315188409751_success_l8bvjx43g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2155315188409751_clickPreventStop_fhku6lbcu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2155315188409751_clickPreventStop_fhku6lbcu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2155315188409751_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2155315188409751_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RegisterView"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="register"
      :headerTitle="pgettext('Register', 'Registers')"
      :items="selectedRegisterId && selectedVersionId ? ds : []"
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'register-entries')"
      footer-totals
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      v-if="loaded"
      @command="evh_2248226175642056_command($event, {})"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <UcRegisterSelector
            :modelValue="states"
            cacheKey="registerView"
            @update:modelValue="
              evh_4615685607825208_updateModelValue($event, {})
            "
          >
          </UcRegisterSelector>
          <ItpButton
            :icon="$config.register.createEntry"
            :text="pgettext('Register', 'Add entry...')"
            size="sm"
            variant="light"
            v-if="isRegisterSelected"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <BDropdown
            size="sm"
            variant="light"
            toggle-class="itp-button border"
            :tooltip="pgettext('Register', 'Set status to selected items')"
            :disabled="
              !selectedItems ||
              !selectedItems.length ||
              (actions.setStatus && actions.setStatus.isRunning)
            "
            v-if="statuses && statuses.length"
          >
            <template #button-content>
              <span>
                <BSpinner
                  small
                  type="grow"
                  class="me-1"
                  v-if="actions.setStatus.isRunning"
                >
                </BSpinner>
                <ItpText
                  :text="pgettext('Register', 'Set status')"
                  class="mx-1"
                >
                </ItpText>
              </span>
            </template>
            <BDropdownItemButton
              :disabled="!selectedItems || !selectedItems.length"
              :key="status"
              v-for="status in statuses"
              @click.prevent.stop="
                evh_7439928539205587_clickPreventStop($event, { status })
              "
            >
              <ItpText :text="status"> </ItpText>
            </BDropdownItemButton>
            <hr />
            <BDropdownItemButton
              :disabled="!selectedItems || !selectedItems.length"
              @click.prevent.stop="
                evh_2155315188409751_clickPreventStop($event, {})
              "
            >
              <ItpText :text="pgettext('Register', 'Clear status')"> </ItpText>
            </BDropdownItemButton>
          </BDropdown>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
